// Quinnipiac University: public college page, official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Quinnipiac's newest posted Common Data Set is 2023-2024
//    (cds_2023-2024_quinnipiac.pdf on the Institutional Research page); no
//    2024-2025 or 2025-2026 edition is posted. So the first-year counts come
//    from the U.S. Department of Education's IPEDS Admissions survey for
//    Fall 2024, first-time degree-seeking students (College Navigator and
//    the ADM2024 complete data file, unit 130226), a year newer than the CDS.
//  - Quinnipiac Undergraduate Admissions: Apply for Undergraduate Programs
//    (the Key Admission Dates table, decision schedule, binding terms) and
//    Admission Requirements (test score policy by program). The pages carry
//    no entry year; they are the pages as posted on 2026-10-01 for the
//    application now open (the FAFSA note on them still names the 2026-27
//    form, so FAFSA dates are not used here).
//  - Quinnipiac Paying for College, Costs and Budgets: Full-Time
//    Undergraduate Costs of Attendance 2026-2027, first-year student living
//    on campus (the items sum to Quinnipiac's own $81,830 total).
//  - College Scorecard, school id 130226 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=130226#admsns',
        read: '2026-10-01',
    },
    ir: {
        label: 'Quinnipiac Institutional Research',
        url: 'https://www.qu.edu/about-quinnipiac/institutional-research/',
        read: '2026-10-01',
    },
    apply: {
        label: 'Quinnipiac Undergraduate Admissions, Apply for Undergraduate Programs',
        url: 'https://www.qu.edu/admissions/undergraduate/apply/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Quinnipiac Undergraduate Admissions, Admission Requirements',
        url: 'https://www.qu.edu/admissions/undergraduate/admission-requirements/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Quinnipiac Paying for College, Costs and Budgets',
        url: 'https://www.qu.edu/paying-for-college/undergraduate/costs-and-budgets/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?130226-Quinnipiac-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'quinnipiac-university',
    kbId: 'quinnipiac_university',
    name: 'Quinnipiac University',
    shortName: 'Quinnipiac',
    aliases: ['QU'],
    location: 'Hamden, CT',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (the Quinnipiac tartan registration);
    // decoration only.
    branding: {
        primary: '#0C2340',
        secondary: '#FFB81C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/quinnipiac_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 23949,
        admitted: 17280,
        enrolled: 1917,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Quinnipiac publishes no first-year acceptance rate by college or major, and its newest posted Common Data Set is the 2023-2024 edition, so the counts above come from the federal IPEDS survey.',
        source: 'ir',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Quinnipiac for a major. Some programs work differently:',
        rows: [
            { school: 'Health programs', requirement: 'Quinnipiac strongly encourages applying Early Decision I or Early Action for full consideration in diagnostic medical sonography, the entry-level master\'s in physician assistant, the entry-level doctor of physical therapy, radiologic sciences and traditional nursing.', source: 'apply' },
            { school: 'Physician assistant, entry-level dual-degree BS/MHS', requirement: 'Official SAT or ACT scores are required. Applicants hear the last week of January.' },
            { school: 'Accelerated dual-degree bachelor\'s/JD (3+3)', requirement: 'Official SAT or ACT scores are required to be considered.' },
            { school: 'Physical therapy, occupational therapy and nursing', requirement: 'Scores are recommended for the BS/Doctor of Physical Therapy and BS/Master of Occupational Therapy dual degrees and for the School of Nursing. These programs, and physician assistant, also expect 4 years of math, and Health Sciences and Nursing applicants 4 years of science.' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-November', binding: true },
            { plan: 'Early Action', deadline: 'November 15', decision: 'Mid-December', binding: false },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'February 1', binding: true },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'Beginning mid-December', binding: false },
        ],
        note: 'Quinnipiac tries to decide Regular Decision applications within two weeks of being complete, and gives priority to those in by February 1; later applications are still read as Regular Decision. The enrollment deposit is due February 1 for Early Decision I, March 1 for Early Decision II and May 1 for Early Action and Regular Decision.',
        source: 'apply',
        binding: 'Early Decision I and II are binding, and students interested in any major may apply Early Decision. If you are not offered admission to your first-choice major, the Early Decision is not binding. Early Action and Regular Decision are not binding.',
        bindingSource: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Quinnipiac is test-optional for most majors: scores are optional in Arts and Sciences, Business, Communications, Engineering and Health Sciences. They are recommended for the accelerated dual-degree programs, physical and occupational therapy and Nursing, and required for the physician assistant program, the 3+3 law program, homeschooled applicants and anyone who must show English proficiency. Scores you enter on the application are considered; to apply test-optional, leave that section blank.',
        source: 'requirements',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'requirements',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 56430 },
            { item: 'Student fees', amount: 2140 },
            { item: 'Technology fees', amount: 860 },
            { item: 'Orientation fee (first year only)', amount: 200 },
            { item: 'Housing and food (on campus)', amount: 18440 },
            { item: 'Books and supplies', amount: 1100 },
            { item: 'Personal expenses', amount: 1810 },
            { item: 'Transportation', amount: 850 },
        ],
        total: 81830,
        note: 'For a full-time first-year student living on campus. Tuition covers 12 to 16 semester hours a term. Books, personal and transportation are estimated allowances, not billed.',
        source: 'costs',
        netPrice: {
            average: 41729,
            byIncome: [
                { income: '$0 to $30,000', amount: 35163 },
                { income: '$30,001 to $48,000', amount: 35914 },
                { income: '$48,001 to $75,000', amount: 37444 },
                { income: '$75,001 to $110,000', amount: 40034 },
                { income: 'Over $110,000', amount: 44609 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Quinnipiac\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
